import React from 'react';
import { clsx } from 'clsx';
import type { BadgeProps } from '@/types/ui';

/**
 * Webcoda-styled Badge component
 * Used for status indicators, labels, and counts
 */
export const Badge: React.FC<BadgeProps> = ({
  variant = 'primary',
  size = 'md',
  dot = false,
  pill = false,
  className,
  children,
  'data-testid': testId,
  ...props
}) => {
  // Base badge classes
  const baseClasses = [
    'inline-flex',
    'items-center',
    'justify-center',
    'font-medium',
    'transition-colors',
    'duration-200',
  ];

  // Variant classes
  const variantClasses = {
    primary: ['bg-primary-100', 'text-primary-800', 'border-primary-200'],
    secondary: ['bg-secondary-100', 'text-secondary-800', 'border-secondary-200'],
    success: ['bg-success-100', 'text-success-800', 'border-success-200'],
    warning: ['bg-warning-100', 'text-warning-800', 'border-warning-200'],
    error: ['bg-error-100', 'text-error-800', 'border-error-200'],
    info: ['bg-blue-100', 'text-blue-800', 'border-blue-200'],
  };

  // Size classes
  const sizeClasses = dot 
    ? {
        sm: ['h-2', 'w-2'],
        md: ['h-3', 'w-3'],
        lg: ['h-4', 'w-4'],
      }
    : {
        sm: ['px-2', 'py-0.5', 'text-xs', 'min-h-[18px]'],
        md: ['px-2.5', 'py-0.5', 'text-xs', 'min-h-[20px]'],
        lg: ['px-3', 'py-1', 'text-sm', 'min-h-[24px]'],
      };

  // Shape classes
  const shapeClasses = dot 
    ? ['rounded-full']
    : pill 
    ? ['rounded-full']
    : ['rounded'];

  // Border classes (subtle border for better definition)
  const borderClasses = ['border'];

  const badgeClasses = clsx(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    shapeClasses,
    borderClasses,
    className
  );

  // For dot variant, don't render children
  if (dot) {
    return (
      <span 
        className={badgeClasses}
        data-testid={testId}
        aria-hidden="true"
        {...props}
      />
    );
  }

  return (
    <span 
      className={badgeClasses}
      data-testid={testId}
      {...props}
    >
      {children}
    </span>
  );
};

/**
 * Status Badge - Specific variant for ticket/user status
 */
export const StatusBadge: React.FC<{
  status: 'new' | 'open' | 'pending' | 'onhold' | 'resolved' | 'closed' | 'cancelled' | 'active' | 'inactive';
  size?: BadgeProps['size'];
  className?: string;
}> = ({ status, size = 'md', className }) => {
  const statusConfig = {
    new: { variant: 'secondary' as const, label: 'New' },
    open: { variant: 'error' as const, label: 'Open' },
    pending: { variant: 'warning' as const, label: 'Pending' },
    onhold: { variant: 'secondary' as const, label: 'On Hold' },
    resolved: { variant: 'success' as const, label: 'Resolved' },
    closed: { variant: 'info' as const, label: 'Closed' },
    cancelled: { variant: 'error' as const, label: 'Cancelled' },
    active: { variant: 'success' as const, label: 'Active' },
    inactive: { variant: 'secondary' as const, label: 'Inactive' },
  };

  const config = statusConfig[status] || { variant: 'primary' as const, label: status };

  return (
    <Badge
      variant={config.variant}
      size={size}
      className={className}
    >
      {config.label}
    </Badge>
  );
};

/**
 * Priority Badge - Specific variant for ticket priority
 */
export const PriorityBadge: React.FC<{
  priority: 'low' | 'medium' | 'high' | 'urgent';
  size?: BadgeProps['size'];
  className?: string;
}> = ({ priority, size = 'md', className }) => {
  const priorityConfig = {
    low: { variant: 'info' as const, label: 'Low' },
    medium: { variant: 'warning' as const, label: 'Medium' },
    high: { variant: 'error' as const, label: 'High' },
    urgent: { variant: 'error' as const, label: 'Urgent' },
  };

  const config = priorityConfig[priority] || { variant: 'primary' as const, label: priority };

  return (
    <Badge
      variant={config.variant}
      size={size}
      className={className}
    >
      {config.label}
    </Badge>
  );
};

/**
 * Count Badge - For displaying numbers/counts
 */
export const CountBadge: React.FC<{
  count: number;
  max?: number;
  size?: BadgeProps['size'];
  variant?: BadgeProps['variant'];
  className?: string;
}> = ({ count, max = 99, size = 'sm', variant = 'error', className }) => {
  const displayCount = count > max ? `${max}+` : count.toString();

  if (count <= 0) return null;

  return (
    <Badge
      variant={variant}
      size={size}
      pill
      className={clsx('min-w-[20px] leading-none', className)}
    >
      {displayCount}
    </Badge>
  );
};

// Attach variants to main Badge component
Object.assign(Badge, { 
  Status: StatusBadge,
  Priority: PriorityBadge,
  Count: CountBadge
});